import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab,
  AppBar,
  Toolbar,
  IconButton,
  Chip,
  LinearProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import StarIcon from '@mui/icons-material/Star';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { subscriptionService } from '../services/subscriptionService';
import PromotionCodeInput from '../components/PromotionCodeInput';
import FamilySharing from '../components/FamilySharing';
import ReportsManager from '../components/ReportsManager';

const Subscription = () => {
  const navigate = useAppNavigation();
  const [subscription, setSubscription] = useState(null);
  const [promotionStatus, setPromotionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingPortal, setProcessingPortal] = useState(false);
  const [showPricingPlans, setShowPricingPlans] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    loadSubscriptionData();
    
    // Refresh subscription data when user returns to page (e.g., after Stripe checkout)
    const handleFocus = () => {
      console.log('Page focused - refreshing subscription data');
      loadSubscriptionData();
    };
    
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('Page became visible - refreshing subscription data');
        loadSubscriptionData();
      }
    };
    
    // Listen for page focus and visibility changes
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Cleanup listeners
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const loadSubscriptionData = async () => {
    setLoading(true);
    try {
      // Force reload by adding timestamp to bypass all caching
      console.log('Loading subscription data...');
      const [subResult, promoResult] = await Promise.all([
        subscriptionService.getCurrentSubscription(),
        subscriptionService.getUserPromotionStatus()
      ]);
      console.log('Subscription data loaded:', subResult);

      if (subResult.success) {
        setSubscription(subResult.subscription);
      }
      
      setPromotionStatus(promoResult);
    } catch (err) {
      setError(err.message || 'Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setProcessingPortal(true);
    try {
      const result = await subscriptionService.createPortalSession();
      if (result.success) {
        // Open Stripe customer portal in new tab
        window.open(result.portal_url, '_blank');
      }
    } catch (err) {
      setError(err.message || 'Failed to open subscription management');
    } finally {
      setProcessingPortal(false);
    }
  };

  const handleUpgrade = (plan) => {
    // For now, let's create a checkout session directly
    handleCreateCheckoutSession(plan);
  };

  const handleCreateCheckoutSession = async (planType) => {
    try {
      setProcessingPortal(true);
      const result = await subscriptionService.createCheckoutSession(planType);
      
      if (result.success) {
        // Redirect to Stripe checkout
        window.location.href = result.checkout_url;
      } else {
        setError(result.message || 'Failed to create checkout session');
      }
    } catch (err) {
      setError(err.message || 'Failed to start checkout process');
    } finally {
      setProcessingPortal(false);
    }
  };

  const getPlanDisplayName = (plan) => {
    const planNames = {
      'free': 'Free',
      'plus': 'Plus',
      'pro': 'Pro',
      'organizer': 'Organizer',
      'mover_pro': 'Mover Pro',
      'custom': 'Custom Plan'
    };
    return planNames[plan] || plan;
  };

  const getPlanColor = (plan) => {
    const colors = {
      'free': 'default',
      'plus': 'primary',
      'pro': 'secondary',
      'organizer': 'success',
      'mover_pro': 'warning',
      'custom': 'info'
    };
    return colors[plan] || 'default';
  };

  const getUsageStats = () => {
    const itemCount = typeof subscription?.item_count === 'number' ? subscription.item_count : null;
    const aiLimit = subscription?.ai_item_limit || 100;
    const aiAssistAvailable = subscription?.ai_assist_available !== false;
    const isFreePlan = (subscription?.plan || 'free') === 'free';
    return { itemCount, aiLimit, aiAssistAvailable, isFreePlan };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppBar position="static">
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => navigate.to.HOME()}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Subscription
            </Typography>
          </Toolbar>
        </AppBar>
        <Container sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Container>
      </Box>
    );
  }

  const { itemCount, aiLimit, aiAssistAvailable, isFreePlan } = getUsageStats();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate.to.HOME()}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Subscription
          </Typography>
        </Toolbar>
      </AppBar>

      <Container component="main" sx={{ flexGrow: 1, py: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Current Plan */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <StarIcon />
            Current Plan
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Chip
              label={getPlanDisplayName(subscription?.plan)}
              color={getPlanColor(subscription?.plan)}
              size="large"
            />
            <Chip
              label={subscription?.status || 'Active'}
              color={subscription?.status === 'active' ? 'success' : 'default'}
              variant="outlined"
            />
          </Box>

          {/* Promotion Status */}
          {promotionStatus?.has_promotion && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">
                Active Promotion: {promotionStatus.promotion?.name}
              </Typography>
              {promotionStatus.expires_at && (
                <Typography variant="body2">
                  Expires: {new Date(promotionStatus.expires_at).toLocaleDateString()}
                </Typography>
              )}
            </Alert>
          )}

          {/* Usage Statistics */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Items Used
                  </Typography>
                  <Typography variant="h6">
                    {itemCount === null ? '—' : itemCount.toLocaleString()} / Unlimited
                  </Typography>
                  {isFreePlan && itemCount !== null && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        AI assist: {Math.min(itemCount, aiLimit)} / {aiLimit} items
                        {!aiAssistAvailable && ' — limit reached, voice input is free forever'}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min((itemCount / aiLimit) * 100, 100)}
                        color={aiAssistAvailable ? 'primary' : 'warning'}
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box display="flex" gap={2} flexWrap="wrap">
              {subscription?.stripe_customer_id && (
                <Button
                  variant="outlined"
                  startIcon={<CreditCardIcon />}
                  onClick={handleManageSubscription}
                  disabled={processingPortal}
                >
                  {processingPortal ? 'Opening...' : 'Manage Billing'}
                </Button>
              )}
              
              {['pro', 'organizer', 'mover_pro'].includes(subscription?.plan) && (
                <Button
                  variant="contained"
                  startIcon={<StarIcon />}
                  onClick={() => {
                    console.log('Navigating to Pro Features...');
                    navigate.to.PRO_FEATURES();
                  }}
                >
                  Pro Features
                </Button>
              )}
              
              {/* Refresh button for development */}
              <Button
                variant="outlined"
                onClick={() => {
                  console.log('Manual refresh triggered');
                  loadSubscriptionData();
                }}
                disabled={loading}
              >
                {loading ? 'Refreshing...' : 'Refresh'}
              </Button>
            </Box>
            
            {subscription?.plan === 'free' && (
              <Button
                variant="contained"
                onClick={() => handleUpgrade('plus')}
              >
                Upgrade to Plus
              </Button>
            )}
            
            {subscription?.plan === 'plus' && (
              <Button
                variant="contained"
                onClick={() => handleUpgrade('pro')}
              >
                Upgrade to Pro
              </Button>
            )}
            
            <Button
              variant="outlined"
              onClick={() => setShowPricingPlans(!showPricingPlans)}
            >
              {showPricingPlans ? 'Hide Plans' : 'View All Plans'}
            </Button>
          </Box>
        </Paper>

        {/* Promotion Code Section */}
        {subscription?.plan === 'free' && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Have a Promotion Code?
            </Typography>
            <PromotionCodeInput
              planType={subscription?.plan || 'plus'}
              onPromotionApplied={(promotion) => {
                if (promotion) {
                  // Reload subscription data to reflect changes
                  loadSubscriptionData();
                }
              }}
            />
          </Paper>
        )}

        {/* Pricing Plans */}
        {showPricingPlans && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Available Plans
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" color="primary">Plus</Typography>
                    <Typography variant="h4" sx={{ mb: 1 }}>$4.99<Typography component="span" variant="body2">/mo</Typography></Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>For most households</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>✅ 5,000 items</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>✅ 20GB storage</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>✅ AI auto-tagging</Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>✅ CSV export</Typography>
                    <Button 
                      variant="contained" 
                      fullWidth 
                      onClick={() => handleUpgrade('plus')}
                      disabled={subscription?.plan !== 'free'}
                    >
                      {subscription?.plan === 'plus' ? 'Current Plan' : 'Upgrade to Plus'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" color="primary">Pro</Typography>
                    <Typography variant="h4" sx={{ mb: 1 }}>$9.99<Typography component="span" variant="body2">/mo</Typography></Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>For power users</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>✅ Unlimited items</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>✅ Advanced AI features</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>✅ Insurance & moving reports</Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>✅ Family sharing (4 members)</Typography>
                    <Button 
                      variant="contained" 
                      fullWidth 
                      onClick={() => handleUpgrade('pro')}
                      disabled={subscription?.plan === 'pro'}
                    >
                      {subscription?.plan === 'pro' ? 'Current Plan' : 'Upgrade to Pro'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" color="primary">Organizer</Typography>
                    <Typography variant="h4" sx={{ mb: 1 }}>$29<Typography component="span" variant="body2">/mo</Typography></Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>For professionals</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>✅ Manage 10 clients</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>✅ Shared dashboard</Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>✅ Bulk import/export</Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>✅ Branded reports</Typography>
                    <Button 
                      variant="contained" 
                      fullWidth 
                      onClick={() => navigate.to.COMING_SOON()}
                    >
                      Subscribe
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Plan Features */}
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Plan Features
          </Typography>
          
          {subscription?.plan === 'free' && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>✅ Unlimited items — free forever</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>✅ Voice descriptions & tags</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>✅ AI assist for your first 100 items</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>✅ CSV export</Typography>
            </Box>
          )}
          
          {subscription?.plan === 'plus' && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>✅ Up to 5,000 items</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>✅ AI auto-tagging</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>✅ CSV export</Typography>
            </Box>
          )}
          
          {subscription?.plan === 'pro' && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>✅ Unlimited items</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>✅ Advanced AI features</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>✅ Insurance & moving reports</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>✅ Family sharing (4 members)</Typography>
            </Box>
          )}
        </Paper>

        {/* Pro Features Section */}
        {['pro', 'organizer', 'mover_pro'].includes(subscription?.plan) && (
          <Box sx={{ mt: 3 }}>
            <Paper sx={{ p: 0 }}>
              <Tabs 
                value={tabValue} 
                onChange={(e, newValue) => setTabValue(newValue)}
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab label="Overview" />
                <Tab label="Family Sharing" />
                <Tab label="Reports & Exports" />
              </Tabs>
              
              <Box sx={{ p: 3 }}>
                {tabValue === 0 && (
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Pro Features Overview
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>
                              Family Sharing
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Invite up to 4 family members to access and manage your inventory together.
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Card>
                          <CardContent>
                            <Typography variant="h6" gutterBottom>
                              Professional Reports
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Generate detailed insurance claims and moving inventory reports with CSV export.
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </Box>
                )}
                
                {tabValue === 1 && (
                  <FamilySharing userSubscription={subscription} />
                )}
                
                {tabValue === 2 && (
                  <ReportsManager userSubscription={subscription} />
                )}
              </Box>
            </Paper>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default Subscription;
